import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  article?: {
    publishedTime?: string;
    author?: string;
    category?: string;
    tags?: string[];
  };
  /** Keep the page out of search engine indexes (e.g. search result pages) */
  noindex?: boolean;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image = 'https://hardnewshungary.com/og-default.jpg',
  url = 'https://hardnewshungary.com',
  type = 'website',
  article,
  noindex = false,
}) => {
  const fullTitle = title === 'Főoldal' ? 'Hard News Hungary' : `${title} | Hard News Hungary`;
  const siteName = 'Hard News Hungary';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      {/* Open Graph (Facebook, LinkedIn, WhatsApp) */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="hu_HU" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Article specific tags */}
      {type === 'article' && article && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.category && <meta property="article:section" content={article.category} />}
          {article.tags?.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Structured Data (JSON-LD) */}
      {type === 'article' && article && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: title,
            description: description,
            image: image,
            datePublished: article.publishedTime,
            author: {
              '@type': 'Person',
              name: article.author || 'Hard News Hungary',
            },
            publisher: {
              '@type': 'Organization',
              name: siteName,
              logo: {
                '@type': 'ImageObject',
                url: 'https://hardnewshungary.com/logo.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': url,
            },
          })}
        </script>
      )}

      {/* Website Organization Schema for Homepage */}
      {type === 'website' && title === 'Főoldal' && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: siteName,
            url: 'https://hardnewshungary.com',
            logo: 'https://hardnewshungary.com/logo.png',
            description: description,
            sameAs: [
              // Add social media profiles if available
            ],
          })}
        </script>
      )}
    </Helmet>
  );
};
